(ns minorodata.app.shared-events
  (:require
   ["ace-builds/src/ace"]
   ["ace-builds/src/ext-language_tools" :as lang-tools]
   ["copy-to-clipboard" :as copy]
   [applied-science.js-interop :as js-interop]
   [clojure.string :as string]
   [day8.re-frame.async-flow-fx]
   [minorodata.app.config]
   [minorodata.app.handlers :refer [inject-cofx reg-cofx reg-event-db
                                    reg-event-fx reg-fx]]
   [minorodata.app.model :refer [->snack]]
   [minorodata.app.utils :as app.utils]
   [minorodata.users.model :refer [->user]]
   [minorodata.utils :as utils]
   [re-posh.core :as re-posh]
   [reitit.frontend.easy :as rt.easy]))


(reg-fx
 :set-local-storage
 (fn [[local-storage-key value]]
   (js/localStorage.setItem local-storage-key (js/JSON.stringify (clj->js value)))))


(reg-fx
 :set-local-storage-edn
 (fn [[local-storage-key value]]
   (js/localStorage.setItem local-storage-key (str value))))


(reg-fx
 :set-local-storage-raw
 (fn [[local-storage-key value]]
   (js/localStorage.setItem local-storage-key value)))


(reg-fx
 :clear-local-storage
 (fn [local-storage-key]
   (js/localStorage.removeItem local-storage-key)))


(reg-fx
 :persist-dark-mode
 (fn [dark-mode]
   (js/localStorage.setItem
    "dark-mode"
    (js/JSON.stringify dark-mode))))


(reg-cofx
 :dark-mode
 (fn [coeffects]
   (assoc coeffects
          :dark-mode
          (js/JSON.parse (js/localStorage.getItem "dark-mode")))))

(reg-cofx
 :navbar
 (fn [coeffects]
   (assoc coeffects
          :navbar
          (merge {:initial-expand true}
                 (js->clj (js/JSON.parse (js/localStorage.getItem "navbar"))
                          :keywordize-keys true)))))


(reg-event-fx
 :app.events/toggle-dark-mode
 (fn [_ [_ skip-reload?]]
   (let [dark-mode? @(re-posh/subscribe [:app/prop :app/dark-mode?])
         dark-mode? (not dark-mode?)]

     (if dark-mode?
       (do
         (when (not (js/document.body.classList.contains "dark"))
           (js/document.body.classList.add "dark"))
         (js/document.body.setAttribute "dark" true))

       (do
         (js/document.body.classList.remove "dark")
         (js/document.body.removeAttribute "dark")))

     {:dispatch                      [:app/update {:app/dark-mode? dark-mode?}]
      :persist-dark-mode             dark-mode?
      :posthog/set-person-properties {:dark-mode dark-mode?}})))


(def deferred-app-init-event
  [{:ms       1
    :dispatch [:app/update {:app/ready? true}]}])

(defn http-cookie-exists? [cookie-name]
  (let [now (js/Date.)
        _ (.setTime now (+ (.getTime now) 1000))
        expires-at (.toUTCString now)
        new-cookie (str cookie-name "=new_value; path=/; expires=" expires-at)]

    (set! (.-cookie js/document) new-cookie)
    (not (string/includes? (.-cookie js/document) cookie-name))))

(defn validate-auth [user token]
  (let [token-exists? (some? token)
        token-valid? (not (app.utils/user-expired? user))]
    (and token-exists? token-valid?)))

(reg-event-fx
 :app.events/preinitialize
 [(inject-cofx :local-user)]
 (fn [cofx _]
   (let [user (:local-user cofx)
         token (:session-token user)
         auth-valid? (validate-auth user token)]
     (if auth-valid?
       {:db (assoc (:db cofx) :app {:user-id (:uid user)
                                    :client-id (:cid user)})
        :unleash/update-context {:userId    (:user user)
                                 :client-id (:cid user)}

        :dispatch-n             [[:unleash/start]
                                 [:sentry/set-user {:email (:user user)}]]}
       {:dispatch [:unleash/start]}))))


(reg-event-fx
 :app.events/set-local-storage
 (fn [_ [key value]]
   {:set-local-storage [key value]}))

(reg-event-fx
 :app.events/clear-local-storage
 (fn [_ [ls-key]]
   {:clear-local-storage ls-key}))


(reg-event-fx
 :app.events/initialize
 [(inject-cofx :local-user)
  (inject-cofx :dark-mode)
  (inject-cofx :navbar)]
 (fn [cofx _]
   (let [user (update (:local-user cofx) :permissions #(mapv keyword %))
         dark-mode (:dark-mode cofx)
         navbar (:navbar cofx)
         token (:session-token user)
         auth-valid? (validate-auth user token)

         {:keys [public name]} @(re-posh/subscribe [:app-route/prop :app-route/options])
         restricted? (-> user :roles first (= "restricted"))]
     (if auth-valid?
       {:dispatch-n     [(when (= :login name)
                           [:app.events/navigate :overview])

                         [:user/add (->user user)]
                         [:app/update {:app/dark-mode? dark-mode
                                       :app/navbar     navbar}]
                         [:source-metadata/add {:source-metadata/metadata {}}]
                         (when-not restricted?
                           [:connections.api/fetch-warehouse-status])
                         [:data-apps.api/fetch-all]
                         [:users.api/fetch-admin-data]]

        :dispatch-later deferred-app-init-event
        :posthog/identify {:user-id (:uid user)
                           :props   {:email       (:user user)
                                     :client-id   (:cid user)
                                     :client-name (:client-name user)
                                     :role        (first (:roles user))}}
        :posthog/group    {:group-type "organization"
                           :group-id   (:cid user)
                           :props      {:name (:client-name user)}}}

       {:dispatch-n     [(cond
                           (not public)
                           [:user.events/redirect-to-login-page]

                           (= :login name)
                           [:user.events/redirect-to-login-page])
                         [:source-metadata/add {:source-metadata/metadata {}}]
                         [:app/update {:app/dark-mode? dark-mode
                                       :app/navbar     navbar}]]
        :dispatch-later []}))))


(reg-fx
 :app.events/navigate!
 (fn [[route params query]]
   (rt.easy/push-state route params query)))

(reg-fx
 :app.events/navigate-replace!
 (fn [[route params query]]
   (rt.easy/replace-state route params query)))

(reg-fx
 :app.events/navigate-query!
 (fn [[new-query]]
   (rt.easy/set-query (fn [query]
                        (->> (merge query new-query)
                             ;; remove nil keys
                             (remove (comp nil? val))
                             (into {}))))))

(reg-fx
 :app.events/replace-query!
 (fn [[new-query]]
   ;; Same as `navigate-query!` but replaces the history entry instead of pushing
   ;; one. For state a view writes to the URL continuously (a timeline window
   ;; being panned, say) — pushing would make Back walk one pan at a time.
   (rt.easy/set-query (fn [query]
                        (->> (merge query new-query)
                             ;; remove nil keys
                             (remove (comp nil? val))
                             (into {})))
                      {:replace true})))

(reg-event-fx
 :app.events/navigate
 (fn [_ [route params query]]
   {:app.events/navigate! [route params query]}))

(reg-event-fx
 :app.events/navigate-replace
 (fn [_ [route params query]]
   {:app.events/navigate-replace! [route params query]}))

(reg-event-fx
 :app.events/navigate-query
 (fn [_ [new-query]]
   {:app.events/navigate-query! [new-query]}))

(reg-event-fx
 :app.events/replace-query
 (fn [_ [new-query]]
   {:app.events/replace-query! [new-query]}))

(reg-event-fx
 :app.events/clear-query
 (fn []
   (let [{:app-route/keys [query]} @(re-posh/subscribe [:app-route/pull])]
     {:dispatch [:app.events/navigate-query (into {} (map (fn [[k]] [k nil]) query))]})))


(reg-event-db
 :app.events/set-render-error
 (fn [db [error]]
   (assoc-in db [:app/error :render] {:error error})))


(reg-event-db
 :app.events/reset-render-error
 (fn [db [error]]
   (assoc-in db [:app/error :render] nil)))


(reg-event-fx
 :app.events/add-snack
 (fn [_ [type message metadata]]
   {:dispatch
    [:snack/add (->snack {:type     type
                          :message  message
                          :active   true
                          :metadata metadata})]}))


(reg-event-fx
 :app.events/close-snack
 (fn [_ [snack-id]]
   {:dispatch       [:snack/update snack-id {:snack/active false}]
    :dispatch-later [{:ms       200
                      :dispatch [:snack/delete snack-id]}]}))


(reg-fx
 :app.events/add-ace-completer
 (fn [completions]
   (let [js-completions (clj->js completions)
         completer #js {:getCompletions
                        (fn [_ session _ _ callback]
                          (let [mode (js-interop/get session :$modeId)]
                            (if (= mode "ace/mode/pgsql")
                              (callback nil js-completions)
                              (callback nil #js []))))}]
     (js-interop/call lang-tools :addCompleter completer))))


(defn set-tab-title [title]
  (js-interop/assoc! js/document :title title))


(def default-title
  "kleene.ai — Simple, powerful data ingestion and processing")


(reg-event-fx
 :app.events/set-page-title
 (fn [_ [title]]
   (cond
     (string? title)
     (set-tab-title title)

     (fn? title)
     (set-tab-title (title))

     :else
     (set-tab-title default-title))
   nil))


(reg-event-fx
 :app.events/copy-to-clipboard
 (fn [_ [data confirm?]]
   (copy data #js {:format "text/plain"})
   (when confirm?
     {:dispatch [:app.events/add-snack :success "Copied to clipboard" {}]})))

(reg-event-fx
 :app.events/update-navbar
 (fn [_ [{:keys [initial-expand links mode]}]]
   (let [current @(re-posh/subscribe [:app/prop :app/navbar])
         links (reduce (fn [acc [k v]] (assoc acc (keyword k) v)) {} links)
         new (-> current
                 (app.utils/assoc-some :initial-expand initial-expand)
                 (update :links #(merge % links))
                 (app.utils/assoc-some :mode mode))]
     (merge
      {:dispatch          [:app/update {:app/navbar new}]
       :set-local-storage ["navbar" new]}
      (when (some? initial-expand)
        {:posthog/set-person-properties {:sidebar-expanded (boolean initial-expand)}})))))

(reg-event-db
 :modals.events/toggle-modal
 (fn [db [modal-id status]]
   (assoc-in db [:app/modals modal-id :open?] status)))

(reg-event-db
 :modals.events/set-modal-params
 (fn [db [modal-id params]]
   (assoc-in db [:app/modals modal-id :params] params)))

(reg-event-fx
 :app.events/update-form-field
 (fn [_ [form-key field-key value]]
   (let [form-state      @(re-posh/subscribe [:form-model/pull [:form-model/id form-key]])
         {:keys [db/id]} (utils/find-first #(= (name (:form-field/name %)) (name field-key)) (:form-model/fields form-state))]
     {:dispatch [:form-field/update id {:form-field/value value} {:replace true}]})))
